import axios from "axios";

export type OutcomeField = { name: string; description: string; type: "string" | "number" | "boolean" };
export type ChatbotConfig = { name: string; description: string; enabled: boolean; system_prompt: string; first_message: string; required_fields: string[]; outcome_fields: OutcomeField[] };
export type ChatbotAgent = ChatbotConfig & { id: string };
export type Conversation = { id: string; phone: string; instance: string; agent_id?: string; agent_name?: string; stage: string; fields: Record<string, string | number | boolean | null>; updated_at: string; ai_paused?: boolean };
export type ChatMedia = { url: string; type: string; filename?: string; size?: number };
export type ChatMessage = { id: string; text: string; reply?: string; status: string; created_at: string; sender?: string; media?: ChatMedia; sent_at?: string };
const base = `${process.env.REACT_APP_BACKEND_URL}/api/v2/chatbot`;
const auth = (token: string) => ({ headers: { Authorization: `Bearer ${token}` } });
export const chatbotApi = {
  agents: async (token: string): Promise<ChatbotAgent[]> => (await axios.get(`${base}/agents`, auth(token))).data.agents,
  save: async (token: string, config: ChatbotConfig, id?: string): Promise<ChatbotAgent> => id
    ? (await axios.put(`${base}/agents/${encodeURIComponent(id)}`, config, auth(token))).data
    : (await axios.post(`${base}/agents`, config, auth(token))).data,
  delete: async (token: string, id: string): Promise<void> => {
    await axios.delete(`${base}/agents/${encodeURIComponent(id)}`, auth(token));
  },
  conversations: async (token: string, agentId: string, offset: number, templateId?: string, instanceName?: string): Promise<{ conversations: Conversation[]; total: number }> =>
    (await axios.get(`${base}/conversations`, { ...auth(token), params: { agent_id: agentId || undefined, template_id: templateId || undefined, instance_name: instanceName || undefined, offset, limit: 50 } })).data,
  instances: async (token: string): Promise<{ instance_name: string; phone_number: string | null }[]> =>
    (await axios.get(`${base}/instances`, auth(token))).data.instances ?? [],
  messages: async (token: string, id: string, offset: number): Promise<ChatMessage[]> =>
    (await axios.get(`${base}/conversations/${encodeURIComponent(id)}/messages`, { ...auth(token), params: { offset, limit: 50 } })).data.messages,
  sendHumanMessage: async (token: string, id: string, payload: { text: string; file?: File | null; templateId?: string; buttons?: { type: string; text: string; value: string; followup_body: string }[] }): Promise<any> => {
    const form = new FormData();
    form.append("text", payload.text || "");
    form.append("pause_ai", "true");
    if (payload.file) form.append("file", payload.file);
    if (payload.templateId) form.append("template_id", payload.templateId);
    if (payload.buttons && payload.buttons.length > 0) {
      form.append("buttons_snapshot", JSON.stringify(payload.buttons.slice(0, 5)));
    }
    return (await axios.post(`${base}/conversations/${encodeURIComponent(id)}/messages`, form, {
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
    })).data;
  },
  setAiPaused: async (token: string, id: string, paused: boolean): Promise<{ thread_id: string; ai_paused: boolean }> =>
    (await axios.put(`${base}/conversations/${encodeURIComponent(id)}/ai-paused`, { paused }, auth(token))).data,
};
export function chatbotError(error: any): string {
  // v2 routes wrap errors as {"error": {"message": "..."}} (app/api/v2/errors.py),
  // not FastAPI's default {"detail": ...} -- reading only `.detail` silently
  // swallowed the real reason (e.g. "Feature category 'chatbot' is not enabled
  // for this organization.") and fell through to the generic message below.
  const v2Message = error?.response?.data?.error?.message;
  if (typeof v2Message === "string" && v2Message) return v2Message;
  const detail = error?.response?.data?.detail;
  if (Array.isArray(detail)) return detail.map((entry: any) => entry.msg).join(". ");
  return typeof detail === "string" ? detail : "Could not complete the request. Please try again.";
}
